import { useFormatters } from '@/hooks/useFormatters';
import { auToneSurfaceSx } from '@/theme/au-compat';
import TimeElapsed from '@components/TimeElapsed';
import { useLingui } from '@lingui/react';
import { Box, Typography } from '@mui/material';
import { useMemo, type FC } from 'react';
import { getAttributes, getTextFormatters, NotificationData } from './utils';

interface CardProps {
  data: NotificationData;
  isFeatured?: boolean;
  animate?: boolean;
  onAnimationEnd?: () => void;
}

/**
 * Un mensaje del feed «Operación en tiempo real»: una superficie teñida por tono, con glifo, texto
 * y hora.
 *
 * **Una sola tinta.** El glifo, el título, su acento, la segunda línea y la hora heredan todos la
 * del tono (`color: inherit`): un relleno teñido lleva una tinta, y la jerarquía la reparten el
 * cuerpo y el peso. El `--au-text-dim` que la glosa pediría está medido contra las superficies,
 * nunca contra un relleno `-bg`, y encima de uno cae por debajo de AA.
 *
 * **El destacado se distingue sólo por ancho** —100% contra 90%—. La sombra proyectada que llevaba
 * es el recurso de algo que flota, y este card está apoyado en la lista como los demás; con los
 * cuatro tonos teñidos, además, ensuciaba el fondo que es justo lo que el tono dice.
 */
export const NotificationCard: FC<CardProps> = ({
  data,
  isFeatured = false,
  animate = false,
  onAnimationEnd,
}) => {
  const { i18n } = useLingui();
  const formatters = useFormatters();
  const { payload, type, timestamp, rawCode } = data;

  // `payload.closeCode` y `rawCode` son el mismo dato por dos caminos: el del mensaje republicado
  // y el de la columna que guardó la fila. Se prefiere el del payload y `rawCode` lo respalda, para
  // que un mensaje que no lo traiga en el payload no pierda la causa del cierre (AMARU-862/US-005).
  const payloadWithCode = useMemo(() => {
    if (rawCode == null || payload?.closeCode != null) return payload;
    return { ...payload, closeCode: rawCode };
  }, [payload, rawCode]);

  const { Icon, tone } = getAttributes(type);
  const { titleFormatter, contentFormatter } = getTextFormatters(type, i18n, formatters);

  // El acento del título realza por **peso**, no por color: una segunda tinta encima de un relleno
  // teñido queda con menos contraste que el enunciado que viene a destacar.
  const title = titleFormatter(payloadWithCode, ({ children }) => (
    <span style={{ fontWeight: 700 }}>{children}</span>
  ));
  const content = contentFormatter ? contentFormatter(payloadWithCode) : null;

  const iconSize = 22;

  return (
    <Box
      onAnimationEnd={onAnimationEnd}
      sx={{
        ...auToneSurfaceSx(tone, { radius: 'card', padding: '10px 18px' }),
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        width: isFeatured ? '100%' : '90%',
        mx: isFeatured ? 0 : 'auto',
        position: 'relative',
        zIndex: isFeatured ? 2 : 1,
        height: '100%',
        overflow: 'hidden',
        // La entrada no viene desde afuera del feed: toma presencia en el espacio que la lista ya
        // le reservó. En X le fabricaría un desborde al área de scroll, que no tiene ese eje.
        ...(animate && {
          transformOrigin: 'center',
          animation: 'notificationEnter 240ms cubic-bezier(0.2, 0.72, 0.25, 1) both',
          '@keyframes notificationEnter': {
            from: { transform: 'scale(0.985)', opacity: 0 },
            to: { transform: 'scale(1)', opacity: 1 },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }),
      }}
    >
      <Box sx={{ display: 'flex', flexShrink: 0, fontSize: iconSize, color: 'inherit' }}>
        <Icon fontSize="inherit" />
      </Box>
      {/* `.oc-notif-text` — columna **centrada** y con mínimo: todas las tarjetas miden lo mismo
          tengan una línea o dos (el `content` es opcional), y sin `justifyContent` la de una sola
          se apoyaba arriba del mínimo, desalineada del glifo y de la hora, que sí están centrados. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 'calc(12.5px * 1.25 + 1px + 11.5px * 1.35)',
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, color: 'inherit' }}>
          {title}
        </Typography>
        {content && (
          <Typography sx={{ fontSize: 11.5, lineHeight: 1.35, mt: '1px', color: 'inherit' }}>
            {content}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexShrink: 0, minWidth: 76, textAlign: 'right', alignSelf: 'flex-start' }}>
        <Typography sx={{ fontSize: 10.5, color: 'inherit' }}>
          <TimeElapsed timestamp={timestamp} />
        </Typography>
      </Box>
    </Box>
  );
};
